import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { Booking } from "@/types/domain";

// GET /bookings/:id/room-suggestion - free rooms for a pending booking (clean first, a group kept together).
export type RoomSuggestion = {
  needs: { roomTypeId: string; name: string; count: number }[];
  roomIds: string[];
  short: { roomTypeId: string; name: string; missing: number }[];
};

export function useRoomSuggestion(booking: Pick<Booking, "id" | "bookingStatus">, selected: string[], select: (roomIds: string[]) => void) {
  const suggestion = useQuery({
    queryKey: ["room-suggestion", booking.id],
    queryFn: () => api<RoomSuggestion>(`/bookings/${booking.id}/room-suggestion`),
    enabled: booking.bookingStatus === "PENDING",
    staleTime: 0,
  });
  // Pre-select the suggestion once; the owner can still change it.
  useEffect(() => {
    if (suggestion.data && !selected.length && suggestion.data.roomIds.length) select(suggestion.data.roomIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion.data]);
  return suggestion;
}

// "2 Deluxe + 3 Standard" - what a booking asks for.
export const needsText = (booking: Booking) =>
  booking.roomTypeLines?.length
    ? booking.roomTypeLines.map((line) => `${line.roomsCount} ${line.roomType?.name ?? "room"}`).join(" + ")
    : `${booking.roomsCount ?? 1} ${booking.roomType?.name ?? "room"}`;

// Room types a booking may be given (a group's lines, or its single type).
export const allowedRoomTypes = (booking: Booking) =>
  booking.roomTypeLines?.length ? booking.roomTypeLines.map((line) => line.roomTypeId) : booking.roomTypeId ? [booking.roomTypeId] : [];
